import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Home } from "lucide-react";

import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/no-permission")({
  component: RouteComponent,
});

interface ForbiddenAccessProps {
  title?: string;
  description?: string;
  homeLink?: string;
  homeLinkText?: string;
}

function RouteComponent({
  title = "Permission Required",
  description = "You don't have the required permissions to access this page. Contact your administrator for assistance.",
  homeLinkText = "Go to App Dashboard",
}: ForbiddenAccessProps) {
  const router = useRouter();

  return (
    <div className="aero:flex aero:min-h-screen aero:w-full aero:flex-col aero:items-center aero:justify-center aero:bg-destructive/5 aero:p-4 aero:text-center">
      <div className="aero:mx-auto aero:mb-6 aero:flex aero:h-24 aero:w-24 aero:items-center aero:justify-center aero:rounded-full aero:bg-destructive/10">
        <AlertTriangle className="aero:h-12 aero:w-12 aero:text-destructive" />
      </div>

      <h1 className="aero:mb-2 aero:text-3xl aero:font-bold aero:tracking-tight">
        {title}
      </h1>
      <p className="aero:mb-8 aero:max-w-md aero:text-muted-foreground">
        {description}
      </p>

      <div className="aero:mb-8 aero:max-w-md aero:rounded-lg aero:border aero:bg-card aero:p-6 aero:shadow-sm">
        <div className="aero:space-y-3 aero:text-left aero:text-sm aero:text-muted-foreground">
          <p className="aero:font-medium">What you can do:</p>
          <ul className="aero:list-inside aero:list-disc aero:space-y-2 aero:pl-4">
            <li>Verify you're using the correct account</li>
            <li>Contact your system administrator for access</li>
            <li>Request the necessary permissions</li>
          </ul>
        </div>
      </div>

      <div className="aero:flex aero:flex-col aero:gap-3 aero:sm:flex-row">
        {router.history.length > 1 && (
          <Button
            intent="secondary"
            design="outline"
            size="lg"
            className="aero:min-w-[200px]"
            onClick={() => router.history.back()}
          >
            <ArrowLeft className="aero:mr-2 aero:h-4 aero:w-4" />
            Go Back
          </Button>
        )}

        <Button
          intent="primary"
          design="solid"
          onClick={() => redirect({ to: "/" })}
          size="lg"
          className="aero:min-w-[200px]"
        >
          <Home className="aero:mr-2 aero:h-4 aero:w-4" />
          {homeLinkText}
        </Button>
      </div>
    </div>
  );
}
